import { useCallback, useState } from 'react'

import { noop } from 'lodash-es'
import { useFocusVisible } from 'use-focus-visible'

/** This hook causes timing issues with blur events in cypress. Temporarily mock it until we have a better solution */
const useFocusVisibleSafe = window.Cypress
  ? () => ({ focusVisible: false, onBlur: noop, onFocus: noop })
  : useFocusVisible

export const useFocusState = ({
  onFocus,
  onBlur,
  active,
}: {
  onFocus?: (e?: any) => void
  onBlur?: (e?: any) => void
  active?: boolean
}): {
  isFocused: boolean
  focusVisible: boolean
  focusProps: {
    onFocus: (e?: any, skipInputFocus?: boolean) => void
    onBlur: (e?: any, skipInputBlur?: boolean) => void
  }
} => {
  const [isFocusedInternal, setIsFocusedInternal] = useState(false)
  const { focusVisible, onBlur: fvBlur, onFocus: fvFocus } = useFocusVisibleSafe()

  const handleFocus = useCallback(
    (e?: any, skipInputFocus?: boolean) => {
      setIsFocusedInternal(true)
      fvFocus()
      if (!skipInputFocus) {
        onFocus?.(e)
      }
    },
    [onFocus, fvFocus],
  )

  const handleBlur = useCallback(
    (e?: any, skipInputBlur?: boolean) => {
      setIsFocusedInternal(false)
      fvBlur()
      if (!skipInputBlur) {
        onBlur?.(e)
      }
    },
    [onBlur, fvBlur],
  )

  return {
    /**
     * True if the component is focused via mouse click or keyboard tab
     */
    isFocused: isFocusedInternal || Boolean(active),
    /**
     * True if the component is focused via keyboard tab
     */
    focusVisible,
    focusProps: {
      onFocus: handleFocus,
      onBlur: handleBlur,
    },
  }
}
